import { LineStyle, type IPriceLine, type ISeriesApi } from 'lightweight-charts';
import type { PositionGroupView } from '@/services/manualTradingApi';
import type { RealtimeSpreadDirection } from '@/services/oppDetectorApi';
import { formatSpreadFundingValue } from '@/lib/chartFormat';
import { normalizeSymbolInput } from '@/lib/exchangeInstId';
import {
  chartSpreadLongShortExchanges,
  instructionThresholdToChartPrice,
} from '@/lib/instructionThresholdLines';
import { labelToManualTradingExchange } from '@/services/manualTradingExchange';

export type PositionOpenSpreadChartLine = {
  spread: number;
};

type TrackedPriceLine = {
  line: IPriceLine;
  series: ISeriesApi<'Line'>;
};

function positionGroupSymbol(group: PositionGroupView): string {
  return (group.long_position?.symbol ?? group.short_position?.symbol ?? '').trim();
}

/** Position long/short legs must match the chart's current spread direction. */
export function positionGroupMatchesChartSpreadDirection(
  group: PositionGroupView,
  exchange1: string,
  exchange2: string,
  direction: RealtimeSpreadDirection,
): boolean {
  const longPos = group.long_position;
  const shortPos = group.short_position;
  if (!longPos || !shortPos) return false;

  const legs = chartSpreadLongShortExchanges(exchange1, exchange2, direction);
  if (!legs) return false;

  const longExchange = labelToManualTradingExchange(longPos.exchange);
  const shortExchange = labelToManualTradingExchange(shortPos.exchange);
  if (!longExchange || !shortExchange) return false;

  return longExchange === legs.longExchange && shortExchange === legs.shortExchange;
}

/** First position group matching the current chart pair and spread direction. */
export function findMatchingPositionGroupForChart(
  groups: PositionGroupView[],
  symbol: string,
  exchange1: string,
  exchange2: string,
  direction: RealtimeSpreadDirection,
): PositionGroupView | null {
  const chartSymbol = normalizeSymbolInput(symbol);
  if (!chartSymbol) return null;

  for (const group of groups) {
    if (group.avg_open_spread == null || !Number.isFinite(group.avg_open_spread)) continue;
    const groupSymbol = normalizeSymbolInput(positionGroupSymbol(group));
    if (groupSymbol !== chartSymbol) continue;
    if (!positionGroupMatchesChartSpreadDirection(group, exchange1, exchange2, direction)) {
      continue;
    }
    return group;
  }
  return null;
}

export function resolvePositionOpenSpreadChartLine(
  group: PositionGroupView | null,
  symbol: string,
  exchange1: string,
  exchange2: string,
  direction: RealtimeSpreadDirection,
): PositionOpenSpreadChartLine | null {
  if (!group) return null;
  if (group.avg_open_spread == null || !Number.isFinite(group.avg_open_spread)) return null;

  const chartSymbol = normalizeSymbolInput(symbol);
  const groupSymbol = normalizeSymbolInput(positionGroupSymbol(group));
  if (!chartSymbol || chartSymbol !== groupSymbol) return null;
  if (!positionGroupMatchesChartSpreadDirection(group, exchange1, exchange2, direction)) {
    return null;
  }

  return { spread: group.avg_open_spread };
}

export function clearPositionOpenSpreadPriceLine(
  trackedLineRef: { current: TrackedPriceLine | null },
): void {
  const tracked = trackedLineRef.current;
  if (!tracked) return;
  try {
    tracked.series.removePriceLine(tracked.line);
  } catch {
    // series/chart may already be destroyed
  }
  trackedLineRef.current = null;
}

export function syncPositionOpenSpreadPriceLine(
  openSeries: ISeriesApi<'Line'> | null,
  line: PositionOpenSpreadChartLine | null,
  chartSpreadScale: number,
  trackedLineRef: { current: TrackedPriceLine | null },
): void {
  clearPositionOpenSpreadPriceLine(trackedLineRef);
  if (!openSeries || !line) return;

  const chartPrice = instructionThresholdToChartPrice(line.spread, chartSpreadScale);
  try {
    const priceLine = openSeries.createPriceLine({
      price: chartPrice,
      color: '#dc2626',
      lineWidth: 2,
      lineStyle: LineStyle.Solid,
      axisLabelVisible: true,
      title: `均开仓 ${formatSpreadFundingValue(chartPrice)}`,
    });
    trackedLineRef.current = { line: priceLine, series: openSeries };
  } catch {
    // ignore draw errors
  }
}

export function reapplyPositionOpenSpreadPriceLine(
  openSeries: ISeriesApi<'Line'> | null,
  line: PositionOpenSpreadChartLine | null,
  chartSpreadScale: number,
  trackedLineRef: { current: TrackedPriceLine | null },
): void {
  syncPositionOpenSpreadPriceLine(openSeries, line, chartSpreadScale, trackedLineRef);
}
